import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, ArrowRight, CheckCircle, Sparkles } from "lucide-react";
import paedagogischeFachkraft from "@/assets/paedagogische-fachkraft-musik.jpg";

export const TermineSection = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-background via-muted/20 to-background relative overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Image */}
          <div className="relative animate-fade-in order-2 lg:order-1">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src={paedagogischeFachkraft} 
                alt="Pädagogische Fachkraft mit Kindern"
                className="w-full h-full object-cover"
              />
              {/* Overlay gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              
              {/* Floating badge */}
              <div className="absolute bottom-6 left-6 right-6">
                <Card className="bg-white/95 backdrop-blur-sm border-none shadow-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">Flexibler Einstieg</p>
                      <p className="text-xs text-muted-foreground">Starte wann du möchtest</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
            
            {/* Decorative element */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-primary/20 to-accent/20 rounded-full blur-3xl -z-10" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-accent/20 to-primary/20 rounded-full blur-3xl -z-10" />
          </div>

          {/* Right: Content */}
          <div className="space-y-8 animate-fade-in order-1 lg:order-2" style={{ animationDelay: "0.1s" }}>
            <div className="space-y-4">
              <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
                Live-Webinare & Termine
              </Badge>
              
              <h2 className="text-4xl lg:text-5xl font-bold leading-tight">
                Deine{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
                  Termine
                </span>
                {" "}im Überblick
              </h2>
              
              <p className="text-lg text-muted-foreground leading-relaxed">
                Alle Live-Webinare der Ausbildung findest du in unserer Terminübersicht. 
                Du kannst jederzeit einsteigen und flexibel an den Terminen teilnehmen, die zu dir passen.
              </p>
            </div>

            {/* Features */}
            <div className="space-y-4">
              <div className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <CheckCircle className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Jederzeit einsteigen</h3>
                  <p className="text-sm text-muted-foreground">
                    Starte deine Ausbildung wann du möchtest – die Webinare wiederholen sich regelmäßig
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Calendar className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">11 Live-Webinare</h3>
                  <p className="text-sm text-muted-foreground">
                    Interaktive Online-Sessions mit unseren erfahrenen Dozent:innen
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                  <Clock className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Flexible Teilnahme</h3>
                  <p className="text-sm text-muted-foreground">
                    Wähle die Termine, die am besten in deinen Zeitplan passen
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
                asChild
              >
                <a href="/termine?kategorie=Ausbildungs-Webinare+(Fachkraft+für+Musik)" target="_blank" rel="noopener noreferrer">
                  Live-Termine ansehen
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </a>
              </Button>
              
              <Button 
                size="lg" 
                variant="outline"
                className="rounded-full px-8 border-2 hover:bg-card transition-all duration-300"
                asChild
              >
                <Link to="/ausbildung/fachkraft-musik/kaufen">
                  Jetzt anmelden
                </Link>
              </Button>
            </div>

            {/* Info Box */}
            <Card className="bg-gradient-to-br from-accent/5 to-primary/5 border-accent/20 p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">Sofort starten möglich</h4>
                  <p className="text-sm text-muted-foreground">
                    Nach deiner Anmeldung erhältst du sofort Zugang zu allen Selbstlernkursen 
                    und kannst dich für die kommenden Live-Webinare anmelden.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
